<template>
  <b-card>
    <b-overlay
      variant="white"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
      :show="pageLoading"
    >
      <b-form @submit.prevent="fetchFineTransactions">
        <b-row class="mb-2">
          <b-col md="3">
            <b-form-input
              v-model="queryParams.number"
              placeholder="External Id"
              clearable
            />
          </b-col>
          <b-col md="3">
            <v-select
              v-model="queryParams.fineState"
              placeholder="Fine State"
              label="label"
              :options="fineStateOptions"
              :reduce="(option) => option.value"
            />
          </b-col>
          <b-col md="3">
            <b-form-datepicker
              v-model="queryParams.created_at_from"
              placeholder="From Date"
              reset-button
              close-button
            />
          </b-col>
          <b-col md="3">
            <b-form-datepicker
              v-model="queryParams.created_at_to"
              placeholder="To Date"
              reset-button
              close-button
            />
          </b-col>
          <b-col md="6" class="mt-1">
            <b-button variant="primary" type="submit">
              Search
            </b-button>
            <b-button
              class="btn-icon rounded-circle ml-1"
              variant="flat-primary"
              @click="fetchFineTransactions"
            >
              <feather-icon icon="RefreshCwIcon" />
            </b-button>
            <b-button
              class="ml-1"
              variant="flat-success"
              @click="exportToCSV"
              :disabled="isExporting"
              v-b-tooltip.hover
              title="Export CSV"
            >
              <feather-icon :icon="isExporting ? 'LoaderIcon' : 'DownloadIcon'" :class="{ 'spin-animation': isExporting }" />
              <span class="ml-50">{{ isExporting ? 'Preparing Export...' : 'Export' }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
      
      <!-- Export Status Alert -->
      <b-alert
        v-if="isExporting"
        variant="info"
        show
        class="mb-2"
      >
        <div class="d-flex align-items-center">
          <b-spinner small class="mr-2" />
          <span>
            <strong>Export in progress...</strong> 
            We're preparing your export file. This may take a few moments. 
          </span>
        </div>
      </b-alert>
      
      <b-table
        responsive
        :items="fines"
        :fields="tableColumns"
        show-empty
        empty-text="No matching fines found"
        striped
      >
        <template #cell(number)="data">
          <div
            style="cursor: pointer;color: #0073bb"
            @click="navigateToTripDetails(data.item.reference_id)"
          >
            <feather-icon icon="ExternalLinkIcon" />
            <span class="ml-1">{{ data.item.number }}</span>
          </div>
        </template>
        <template #cell(actions)="data">
          <b-button
            variant="primary"
            :disabled="data.item.fine_state !== 'in_review'"
            @click="updateFineTransaction(data.item.id, 'processed')"
          >
            <span>Process</span>
          </b-button>
          <b-button
            variant="danger"
            class="ml-2"
            :disabled="data.item.fine_state !== 'in_review'"
            @click="updateFineTransaction(data.item.id, 'rejected')"
          >
            <span>Reject</span>
          </b-button>
        </template>
        <template #cell(created_at)="data">
          <span>{{
            new Date(`${data.item.created_at}Z`).toLocaleString()
          }}</span>
        </template>
      </b-table>
      <base-pagination
        v-model="queryParams.page"
        :total="totalFines"
        @new-per-page="queryParams.perPage = $event"
      />
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BOverlay,
  BTable,
  BRow,
  BCol,
  BCard,
  BButton,
  BForm,
  BFormInput,
  BFormDatepicker,
  BAlert,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import useFineList from "../hooks/useFineList";
import BasePagination from "@/UI/BasePagination.vue";

import { onBeforeUnmount } from "@vue/composition-api";

export default {
  components: {
    BOverlay,
    BTable,
    BCol,
    BRow,
    BCard,
    BButton,
    vSelect,
    BasePagination,
    BForm,
    BFormInput,
    BFormDatepicker,
    BAlert,
    BSpinner,
  },
  setup() {
    const fineListHook = useFineList();
    
    // Clean up polling when component is destroyed
    onBeforeUnmount(() => {
      fineListHook.stopPolling();
    });
    
    return {
      ...fineListHook,
      fineStateOptions: [
        {
          label: "Processed",
          value: "processed",
        },
        {
          label: "In Review",
          value: "in_review",
        },
        {
          label: "Rejected",
          value: "rejected",
        },
      ],
    };
  },
};
</script>

<style scoped>
.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
